import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { AnimatePresence, motion } from "motion/react";

import useWizard from "../useWizard";
import { ACCENT_COLOR } from "../theme";
import DatePicker from "Utilities/DatePicker";
import FnrShineButton from "Utilities/FnrShineButton";

function AutoFocusPriceInput({ value, onChange, onBlur }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (el) {
      el.focus();
      el.select();
    }
  }, []);

  return (
    <PriceInput
      ref={ref}
      type="text"
      inputMode="decimal"
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        if (/^\d*\.?\d{0,2}$/.test(v)) {
          onChange(v);
        }
      }}
      onBlur={onBlur}
      placeholder="0.00"
    />
  );
}

function AutoFocusFeeInput({ value, onChange, onBlur }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) { el.focus(); el.select(); }
  }, []);
  return (
    <FeeInput
      ref={ref}
      type="text"
      inputMode="decimal"
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        if (/^\d*\.?\d{0,2}$/.test(v)) onChange(v);
      }}
      onBlur={onBlur}
      placeholder="0.00"
    />
  );
}

function GeneralPricing() {
  const { data, updateField } = useWizard();
  const pricing = data.pricing;
  const [priceError, setPriceError] = useState(false);
  const [capError, setCapError] = useState("");
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const validateCap = (baseFee, capValue) => {
    if (!capValue) { setCapError(""); return; }
    const num = parseFloat(capValue);
    const baseNum = parseFloat(baseFee);
    const baseValid = baseFee && !isNaN(baseNum) && baseNum >= 2;
    if (baseValid && (isNaN(num) || num < baseNum)) {
      setCapError("The maximum family price must be at least as much as the student price.");
    } else if (isNaN(num) || num < 2) {
      setCapError("The price must be at least $2");
    } else {
      setCapError("");
    }
  };

  const handlePriceBlur = () => {
    const num = parseFloat(pricing.baseFee);
    setPriceError(pricing.baseFee !== "" && (isNaN(num) || num < 2));
    if (pricing.familyCap) validateCap(pricing.baseFee, pricing.familyCapValue);
  };

  const handleCapBlur = () => {
    validateCap(pricing.baseFee, pricing.familyCapValue);
  };

  return (
    <Content>
      <div>
        <Title className="merri">General Pricing</Title>
        <Subtitle className="raleway">
          Set the base price for your RE program
        </Subtitle>
      </div>

      <FormCenter>
        <PricingList>
          <PricingRow>
            <RowIcon className="fas fa-dollar-sign" />
            <RowContent>
              <RowLabel>Price per Student</RowLabel>
              <PriceFieldWrap style={{ marginTop: 6 }}>
                <PriceInputWrap $error={priceError}>
                  <PricePrefix $active={!!pricing.baseFee}>$</PricePrefix>
                  <PriceInput
                    type="text"
                    inputMode="decimal"
                    value={pricing.baseFee}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (/^\d*\.?\d{0,2}$/.test(v)) {
                        updateField("pricing", "baseFee", v);
                        if (priceError) setPriceError(false);
                      }
                    }}
                    onBlur={handlePriceBlur}
                    placeholder="0.00"
                  />
                </PriceInputWrap>
                <AnimatePresence>
                  {priceError && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: "auto" }}
                      exit={{ height: 0 }}
                      transition={{ duration: 0.2 }}
                      style={{ overflow: "hidden" }}
                    >
                      <PriceError>
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.15 }}
                        >
                          The price must be at least $2
                        </motion.span>
                      </PriceError>
                    </motion.div>
                  )}
                </AnimatePresence>
              </PriceFieldWrap>
            </RowContent>
          </PricingRow>
          <FamilyCapRow
            $alt
            onClick={() => {
              if (pricing.familyCap) {
                updateField("pricing", "familyCap", false);
                setCapError("");
              } else {
                const base = parseFloat(pricing.baseFee);
                const defaultCap = (!pricing.baseFee || isNaN(base)) ? "" : (base * 3).toFixed(2).replace(/\.00$/, "");
                updateField("pricing", "familyCapValue", defaultCap);
                updateField("pricing", "familyCap", true);
              }
            }}
          >
            <RowIcon className="fas fa-users" />
            <RowContent>
              <FamilyCapTop>
                <RowLabel>Apply a maximum price per family</RowLabel>
                <PricingCheckbox $on={pricing.familyCap}>
                  {pricing.familyCap && <i className="fas fa-check" />}
                </PricingCheckbox>
              </FamilyCapTop>
              <AnimatePresence initial={mounted}>
                {pricing.familyCap && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FamilyCapInputInner>
                      <PriceFieldWrap>
                        <PriceInputWrap $error={!!capError}>
                          <PricePrefix $active={!!pricing.familyCapValue}>$</PricePrefix>
                          <AutoFocusPriceInput
                            value={pricing.familyCapValue}
                            onChange={(v) => {
                              updateField("pricing", "familyCapValue", v);
                              if (capError) setCapError("");
                            }}
                            onBlur={handleCapBlur}
                          />
                        </PriceInputWrap>
                        <AnimatePresence>
                          {capError && (
                            <motion.div
                              initial={{ height: 0 }}
                              animate={{ height: "auto" }}
                              exit={{ height: 0 }}
                              transition={{ duration: 0.2 }}
                              style={{ overflow: "hidden" }}
                            >
                              <PriceError>
                                <motion.span
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  {capError}
                                </motion.span>
                              </PriceError>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </PriceFieldWrap>
                    </FamilyCapInputInner>
                  </motion.div>
                )}
              </AnimatePresence>
            </RowContent>
          </FamilyCapRow>
        </PricingList>

        <PricingList>
          <PricingRow onClick={() => updateField("pricing", "requirePayment", !pricing.requirePayment)} style={{ overflow: "hidden" }}>
            <RowIcon className="fas fa-lock" />
            <RowContent>
              <RowLabel style={{ padding: "4px 0" }}>Require payment to sign up</RowLabel>
              <AnimatePresence initial={mounted}>
                {pricing.requirePayment && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                  >
                    <RequirePaymentDesc>
                      We'll require payment within 5 days of signing up or the registration will be dropped. You'll receive an email notification before anyone is dropped.{" "}
                      <HelpLink href="https://help.flocknote.com" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                        <i className="fas fa-circle-question" />
                      </HelpLink>
                    </RequirePaymentDesc>
                  </motion.div>
                )}
              </AnimatePresence>
            </RowContent>
            <PricingCheckbox $on={pricing.requirePayment}>
              {pricing.requirePayment && <i className="fas fa-check" />}
            </PricingCheckbox>
          </PricingRow>
        </PricingList>
      </FormCenter>
    </Content>
  );
}

function AdditionalFees() {
  const { data, updateField, nextSubStep, setBackOverride } = useWizard();
  const pricing = data.pricing;
  const isDeclined = pricing.additionalFeesDeclined === true;
  const [showDeclinedSelected, setShowDeclinedSelected] = useState(isDeclined);
  const [showFees, setShowFees] = useState(() => pricing.additionalFeesNeeded === true);
  const hasToggled = useRef(false);
  const [feeErrors, setFeeErrors] = useState({});
  const skipFeeAnim = useRef(showFees);
  useEffect(() => { skipFeeAnim.current = false; }, [showFees]);

  useEffect(() => {
    if (showFees) {
      setBackOverride(() => {
        hasToggled.current = true;
        updateField("pricing", "additionalFeesNeeded", false);
        setShowFees(false);
        return true;
      });
    } else {
      setBackOverride(null);
    }
    return () => setBackOverride(null);
  }, [showFees, setBackOverride, updateField]);

  const fees = pricing.additionalFees || {};
  const classes = (data.generalInfo.classes || [])
    .map((name, i) => ({ name, idx: i }))
    .filter(c => c.name.trim());

  const removingRef = useRef({});
  const focusKeyRef = useRef({});

  const toggleFee = (idx) => {
    if (idx in fees) {
      removingRef.current[idx] = true;
      const without = { ...fees };
      delete without[idx];
      if (feeErrors[idx]) {
        setFeeErrors(prev => { const next = { ...prev }; delete next[idx]; return next; });
        setTimeout(() => {
          delete removingRef.current[idx];
          updateField("pricing", "additionalFees", without);
        }, 200);
      } else {
        delete removingRef.current[idx];
        setFeeErrors(prev => { const next = { ...prev }; delete next[idx]; return next; });
        updateField("pricing", "additionalFees", without);
      }
    } else {
      focusKeyRef.current[idx] = (focusKeyRef.current[idx] || 0) + 1;
      setFeeErrors(prev => { const next = { ...prev }; delete next[idx]; return next; });
      updateField("pricing", "additionalFees", { ...fees, [idx]: "" });
    }
  };

  const updateFeeValue = (idx, value) => {
    updateField("pricing", "additionalFees", { ...fees, [idx]: value });
  };

  const handleFeeBlur = (idx) => {
    setTimeout(() => {
      if (removingRef.current[idx]) return;
      const value = (pricing.additionalFees || {})[idx];
      if (value === undefined) return;
      const num = parseFloat(value);
      setFeeErrors(prev => ({
        ...prev,
        [idx]: value !== "" && (isNaN(num) || num < 2),
      }));
    }, 100);
  };

  const handleYes = () => {
    hasToggled.current = true;
    skipFeeAnim.current = true;
    updateField("pricing", "additionalFeesNeeded", true);
    updateField("pricing", "additionalFeesDeclined", false);
    if (showDeclinedSelected) {
      setShowDeclinedSelected(false);
      requestAnimationFrame(() => setShowFees(true));
    } else {
      setShowFees(true);
    }
  };

  const handleNo = () => {
    hasToggled.current = true;
    updateField("pricing", "additionalFeesNeeded", false);
    updateField("pricing", "additionalFeesDeclined", true);
    if (showDeclinedSelected) {
      nextSubStep();
    } else {
      setShowDeclinedSelected(true);
      setTimeout(() => nextSubStep(), 300);
    }
  };

  return (
    <Content>
      <div>
        <Title className="merri">Additional Fees</Title>
        <Subtitle className="raleway">
          Some classes may have additional fees for sacramental preparation or materials.
        </Subtitle>
      </div>

      <FormCenter>
        <AnimatePresence mode="wait">
          {!showFees ? (
            <motion.div
              key="choices"
              initial={hasToggled.current ? "initial" : false}
              animate="enter"
              exit="exit"
            >
              <ChoiceRows>
                <motion.div variants={{
                  initial: { opacity: 0, y: -40 },
                  enter: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 150, damping: 15, delay: 0.08 } },
                  exit: { opacity: 0, y: -40, transition: { duration: 0.2, delay: 0 } },
                }}>
                  <ChoiceRow onClick={handleYes}>
                    <ChoiceRowInner>
                      <ChoiceIcon className="fas fa-receipt" />
                      <ChoiceLabel>Some of my classes have sacramental or materials fees that need to be added.</ChoiceLabel>
                    </ChoiceRowInner>
                  </ChoiceRow>
                </motion.div>
                <motion.div variants={{
                  initial: { opacity: 0, y: -40 },
                  enter: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 150, damping: 15, delay: 0 } },
                  exit: { opacity: 0, y: -40, transition: { duration: 0.2, delay: 0.08 } },
                }}>
                  <ChoiceRow onClick={handleNo} $selected={showDeclinedSelected}>
                    <ChoiceRowInner>
                      <ChoiceIcon className="fas fa-xmark" />
                      <ChoiceLabel>I don't need to add any additional fees.</ChoiceLabel>
                    </ChoiceRowInner>
                    <CheckIcon className="fas fa-circle-check" $visible={showDeclinedSelected} />
                  </ChoiceRow>
                </motion.div>
              </ChoiceRows>
            </motion.div>
          ) : (
            <motion.div
              key="fees"
              initial={hasToggled.current ? { opacity: 0, y: 40 } : false}
              animate={{ opacity: 1, y: 0, transition: { type: "spring", stiffness: 150, damping: 15 } }}
              exit={{ opacity: 0, y: 40, transition: { duration: 0.3 } }}
              style={{ width: "100%" }}
            >
              <FeesList>
                {classes.map((c, i) => {
                  const hasFee = c.idx in fees;
                  const feeValue = fees[c.idx] || "";
                  const error = feeErrors[c.idx];
                  return (
                    <FeeRow key={c.idx} $alt={i % 2 === 1} $last={i === classes.length - 1}>
                      <FeeRowTop>
                        <FeeClassName>{c.name}</FeeClassName>
                        <AnimatePresence>
                          {hasFee && (
                            <motion.div
                              initial={skipFeeAnim.current ? false : { opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              transition={{ type: "spring", stiffness: 400, damping: 25 }}
                              style={{ originX: 1, originY: 0.5 }}
                            >
                              <FeeFieldWrap>
                                <FeeInputWrap $error={!!error}>
                                  <FeePrefix $active={!!feeValue}>$</FeePrefix>
                                  <AutoFocusFeeInput
                                    key={focusKeyRef.current[c.idx] || 0}
                                    value={feeValue}
                                    onChange={(v) => {
                                      updateFeeValue(c.idx, v);
                                      if (error) setFeeErrors(prev => ({ ...prev, [c.idx]: false }));
                                    }}
                                    onBlur={() => handleFeeBlur(c.idx)}
                                  />
                                </FeeInputWrap>
                                <AnimatePresence>
                                  {error && (
                                    <motion.div
                                      initial={{ height: 0 }}
                                      animate={{ height: "auto" }}
                                      exit={{ height: 0 }}
                                      transition={{ duration: 0.2 }}
                                      style={{ overflow: "hidden", width: 0, minWidth: "100%" }}
                                    >
                                      <FeeError>
                                        <motion.span
                                          initial={{ opacity: 0 }}
                                          animate={{ opacity: 1 }}
                                          exit={{ opacity: 0 }}
                                          transition={{ duration: 0.15 }}
                                        >
                                          The price must be at least $2
                                        </motion.span>
                                      </FeeError>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </FeeFieldWrap>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        <FeePill $active={hasFee} onClick={() => toggleFee(c.idx)}>
                          <PillInner>
                            <AnimatePresence initial={false}>
                              {hasFee ? (
                                <PillContent
                                  key="trash"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  <i className="fas fa-trash-can" />
                                </PillContent>
                              ) : (
                                <PillContent
                                  key="add"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  Add Fees
                                </PillContent>
                              )}
                            </AnimatePresence>
                          </PillInner>
                        </FeePill>
                      </FeeRowTop>
                    </FeeRow>
                  );
                })}
              </FeesList>
            </motion.div>
          )}
        </AnimatePresence>
      </FormCenter>
    </Content>
  );
}

function formatDiscountDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  return `${months[m - 1]} ${d}, ${y}`;
}

function addDays(dateStr, days) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function usePickerPosition(triggerRef, pickerRef, open) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const pickerH = pickerRef.current?.offsetHeight || 300;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    if (spaceBelow >= pickerH + 6) {
      setPos({ left: rect.left, top: rect.bottom + 6, bottom: "auto", anchor: "top" });
    } else if (spaceAbove >= pickerH + 6) {
      setPos({ left: rect.left, bottom: window.innerHeight - rect.top + 6, top: "auto", anchor: "bottom" });
    } else {
      setPos({ left: rect.left, bottom: 20, top: "auto", anchor: "center" });
    }
  }, [triggerRef, pickerRef]);

  useEffect(() => {
    if (!open) return;
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open, updatePos]);

  return [pos, updatePos];
}

function PricingSection({ icon, label, description, feeField, capField, capValueField, dateField, dateLabel, pricing, updateField, rowOffset, minDate, maxDate, onDateChange }) {
  const [feeError, setFeeError] = useState(false);
  const [capError, setCapError] = useState("");

  // Pre-fill fee from baseFee on first mount if empty
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current) return;
    prefilled.current = true;
    if (!pricing[feeField] && pricing.baseFee) {
      updateField("pricing", feeField, pricing.baseFee);
    }
    if (!pricing[capValueField] && pricing.familyCapValue) {
      updateField("pricing", capValueField, pricing.familyCapValue);
    }
    if (pricing.familyCap && !pricing[capField]) {
      updateField("pricing", capField, true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [pickerOpen, setPickerOpen] = useState(false);
  const triggerRef = useRef(null);
  const pickerRef = useRef(null);
  const [pickerPos] = usePickerPosition(triggerRef, pickerRef, pickerOpen);

  useEffect(() => {
    if (!pickerOpen) return;
    const handleClick = (e) => {
      if (triggerRef.current?.contains(e.target) || pickerRef.current?.contains(e.target)) return;
      setPickerOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [pickerOpen]);

  const fee = pricing[feeField];
  const cap = pricing[capField];
  const capValue = pricing[capValueField];
  const dateValue = dateField ? pricing[dateField] : null;

  const validateCap = (feeVal, capVal) => {
    if (!capVal) { setCapError(""); return; }
    const num = parseFloat(capVal);
    const feeNum = parseFloat(feeVal);
    const feeValid = feeVal && !isNaN(feeNum) && feeNum >= 2;
    if (feeValid && (isNaN(num) || num < feeNum)) {
      setCapError("The maximum family price must be at least as much as the student price.");
    } else if (isNaN(num) || num < 2) {
      setCapError("The price must be at least $2");
    } else {
      setCapError("");
    }
  };

  const handleFeeBlur = () => {
    const num = parseFloat(fee);
    setFeeError(fee !== "" && (isNaN(num) || num < 2));
    if (cap) validateCap(fee, capValue);
  };

  const handleCapBlur = () => validateCap(fee, capValue);

  const base = rowOffset || 0;

  return (
    <div>
      <SectionTitle>
        <SectionTitleIcon className={`fas ${icon}`} />
        {label}
      </SectionTitle>
      <PricingList>
        {description && (
          <SectionDescRow>
            {description}
          </SectionDescRow>
        )}
        {dateField && (
          <PricingRow $alt={(base + (description ? 1 : 0)) % 2 === 1}>
            <RowIcon className="fas fa-calendar" />
            <RowLabel style={{ flex: 1 }}>{dateLabel || "Date"}</RowLabel>
            <DateInputWrap
              ref={triggerRef}
              $active={pickerOpen}
              $hasValue={!!dateValue}
              onClick={() => setPickerOpen((o) => !o)}
            >
              <DateInputText $hasValue={!!dateValue}>{dateValue ? formatDiscountDate(dateValue) : "Pick a date"}</DateInputText>
              <i className="fas fa-calendar" />
            </DateInputWrap>
          </PricingRow>
        )}

        <PricingRow $alt={(base + (description ? 1 : 0) + (dateField ? 1 : 0)) % 2 === 1}>
          <RowIcon className="fas fa-dollar-sign" />
          <RowLabel style={{ flex: 1 }}>Price per student</RowLabel>
          <PriceFieldWrap style={{ maxWidth: 105 }}>
            <PriceInputWrap $error={feeError}>
              <PricePrefix $active={!!fee}>$</PricePrefix>
              <PriceInput
                type="text"
                inputMode="decimal"
                value={fee}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^\d*\.?\d{0,2}$/.test(v)) {
                    updateField("pricing", feeField, v);
                    if (feeError) setFeeError(false);
                  }
                }}
                onBlur={handleFeeBlur}
                placeholder="0.00"
              />
            </PriceInputWrap>
            <AnimatePresence>
              {feeError && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ overflow: "hidden" }}
                >
                  <PriceError>
                    <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                      The price must be at least $2
                    </motion.span>
                  </PriceError>
                </motion.div>
              )}
            </AnimatePresence>
          </PriceFieldWrap>
        </PricingRow>

        {pricing.familyCap && (
          <PricingRow $alt={(base + (description ? 1 : 0) + (dateField ? 2 : 1)) % 2 === 1}>
            <RowIcon className="fas fa-users" />
            <RowLabel style={{ flex: 1 }}>Maximum price per family</RowLabel>
            <PriceFieldWrap style={{ maxWidth: 105 }}>
              <PriceInputWrap $error={!!capError}>
                <PricePrefix $active={!!capValue}>$</PricePrefix>
                <PriceInput
                  type="text"
                  inputMode="decimal"
                  value={capValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^\d*\.?\d{0,2}$/.test(v)) {
                      updateField("pricing", capValueField, v);
                      if (capError) setCapError("");
                    }
                  }}
                  onBlur={handleCapBlur}
                  placeholder="0.00"
                />
              </PriceInputWrap>
              <AnimatePresence>
                {capError && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ overflow: "hidden" }}
                  >
                    <PriceError>
                      <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                        {capError}
                      </motion.span>
                    </PriceError>
                  </motion.div>
                )}
              </AnimatePresence>
            </PriceFieldWrap>
          </PricingRow>
        )}
      </PricingList>

      {dateField && createPortal(
        <AnimatePresence>
          {pickerOpen && (
            <DiscountPickerPortal
              ref={pickerRef}
              style={{
                top: pickerPos.top,
                bottom: pickerPos.bottom,
                left: pickerPos.left,
                transformOrigin: pickerPos.anchor === "top" ? "top center" : pickerPos.anchor === "bottom" ? "bottom center" : "center center",
              }}
              initial={{ opacity: 0, scale: 0.7, rotateX: -35 }}
              animate={{ opacity: 1, scale: 1, rotateX: 0, transition: { type: "spring", stiffness: 500, damping: 25 } }}
              exit={{ opacity: 0, scale: 0.7, rotateX: -35, transition: { duration: 0.15 } }}
            >
              <DatePicker
                value={dateValue}
                onChange={(v) => {
                  updateField("pricing", dateField, v);
                  if (onDateChange) onDateChange(v);
                }}
                onClose={() => setPickerOpen(false)}
                minDate={minDate || new Date().toISOString().slice(0, 10)}
                maxDate={maxDate || "2030-01-15"}
              />
            </DiscountPickerPortal>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}

function AdvancedPricingModal({ open, onClose, pricing, updateField }) {
  return createPortal(
    <AnimatePresence>
      {open && (
        <ModalBackdrop
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <ModalCard
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0, transition: { type: "spring", stiffness: 400, damping: 28 } }}
            exit={{ opacity: 0, scale: 0.9, y: 20, transition: { duration: 0.15 } }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalCloseBtn onClick={onClose}>
              <i className="fas fa-circle-xmark" />
            </ModalCloseBtn>
            <ModalTitle className="merri">Advanced Pricing</ModalTitle>
            <ModalText>
              You can finalize your setup now and adjust advanced pricing options later. If you'd like, a Happiness Engineer can reach out to help you set things up.
            </ModalText>
            <ModalOptionRow onClick={() => updateField("pricing", "advancedPricingRequested", !pricing.advancedPricingRequested)}>
              <ModalOptionIcon className="fas fa-headset" />
              <ModalOptionLabel>Have a Happiness Engineer reach out to help set up my RE pricing.</ModalOptionLabel>
              <PricingCheckbox $on={pricing.advancedPricingRequested}>
                {pricing.advancedPricingRequested && <i className="fas fa-check" />}
              </PricingCheckbox>
            </ModalOptionRow>
            <ModalOkWrap $disabled={false}>
              <FnrShineButton onClick={onClose} borderRadius={100}>
                <ModalOkInside>OK <i className="fas fa-circle-check" /></ModalOkInside>
              </FnrShineButton>
            </ModalOkWrap>
          </ModalCard>
        </ModalBackdrop>
      )}
    </AnimatePresence>,
    document.body
  );
}

function DiscountsAndMore() {
  const { data, updateField, goToStep, setNextOverride, setModalOpen } = useWizard();
  const pricing = data.pricing;
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    setNextOverride(() => {
      if (pricing.earlyBirdPricing || pricing.latePricing) {
        goToStep(4, 3);
      } else if (pricing.memberPricing || pricing.catechistPricing) {
        goToStep(4, 4);
      } else {
        goToStep(5, 0, true);
      }
      return true;
    });
    return () => setNextOverride(null);
  }, [setNextOverride, goToStep, pricing.earlyBirdPricing, pricing.latePricing, pricing.memberPricing, pricing.catechistPricing]);

  const openAdvanced = () => {
    setShowAdvanced(true);
    setModalOpen(true);
  };

  const closeAdvanced = () => {
    setShowAdvanced(false);
    setModalOpen(false);
    if (pricing.advancedPricingRequested) {
      updateField("pricing", "earlyBirdPricing", false);
      updateField("pricing", "latePricing", false);
      updateField("pricing", "memberPricing", false);
      updateField("pricing", "catechistPricing", false);
      goToStep(5, 0, true);
    }
  };

  const toggleOption = (field) => {
    updateField("pricing", field, !pricing[field]);
  };

  return (
    <Content>
      <div>
        <Title className="merri">Discounts & More</Title>
        <Subtitle className="raleway">
          Which of these options apply to your pricing?
        </Subtitle>
      </div>

      <FormCenter>
        <CategoryLabel>Time-Based Pricing</CategoryLabel>
        <PricingList>
          <CategoryRow onClick={() => toggleOption("earlyBirdPricing")}>
            <RowIcon className="fas fa-clock" />
            <RowContent>
              <RowLabel>Early Bird Pricing</RowLabel>
              <DiscountDesc>Reduced rate for early registration</DiscountDesc>
            </RowContent>
            <PricingCheckbox $on={pricing.earlyBirdPricing}>
              {pricing.earlyBirdPricing && <i className="fas fa-check" />}
            </PricingCheckbox>
          </CategoryRow>
          <CategoryRow $alt onClick={() => toggleOption("latePricing")}>
            <RowIcon className="fas fa-hourglass-end" />
            <RowContent>
              <RowLabel>Late Pricing</RowLabel>
              <DiscountDesc>Higher rate for late registration</DiscountDesc>
            </RowContent>
            <PricingCheckbox $on={pricing.latePricing}>
              {pricing.latePricing && <i className="fas fa-check" />}
            </PricingCheckbox>
          </CategoryRow>
        </PricingList>

        <CategoryLabel>More Options</CategoryLabel>
        <PricingList>
          <CategoryRow onClick={() => toggleOption("memberPricing")}>
            <RowIcon className="fas fa-id-card" />
            <RowContent>
              <RowLabel>Member Pricing</RowLabel>
              <DiscountDesc>Special rate for parish members</DiscountDesc>
            </RowContent>
            <PricingCheckbox $on={pricing.memberPricing}>
              {pricing.memberPricing && <i className="fas fa-check" />}
            </PricingCheckbox>
          </CategoryRow>
          <CategoryRow $alt onClick={() => toggleOption("catechistPricing")}>
            <RowIcon className="fas fa-chalkboard-user" />
            <RowContent>
              <RowLabel>Catechist Pricing</RowLabel>
              <DiscountDesc>Discounted rate for volunteer catechists</DiscountDesc>
            </RowContent>
            <PricingCheckbox $on={pricing.catechistPricing}>
              {pricing.catechistPricing && <i className="fas fa-check" />}
            </PricingCheckbox>
          </CategoryRow>
        </PricingList>

      </FormCenter>

      <AdvancedButton onClick={openAdvanced}>
        <AdvancedButtonInner>
          <i className="fas fa-wand-magic-sparkles" /> I have more advanced pricing needs
        </AdvancedButtonInner>
      </AdvancedButton>

      <AdvancedPricingModal
        open={showAdvanced}
        onClose={closeAdvanced}
        pricing={pricing}
        updateField={updateField}
      />
    </Content>
  );
}

function EarlyLatePricing() {
  const { data, updateField, goToStep, setNextOverride, setBackOverride } = useWizard();
  const pricing = data.pricing;

  useEffect(() => {
    setNextOverride(() => {
      if (pricing.memberPricing || pricing.catechistPricing) {
        goToStep(4, 4);
      } else {
        goToStep(5, 0, true);
      }
      return true;
    });
    setBackOverride(() => {
      goToStep(4, 2);
      return true;
    });
    return () => {
      setNextOverride(null);
      setBackOverride(null);
    };
  }, [setNextOverride, setBackOverride, goToStep, pricing.memberPricing, pricing.catechistPricing]);

  const hasBoth = pricing.earlyBirdPricing && pricing.latePricing;

  const earlyMaxDate = hasBoth && pricing.lateDate ? addDays(pricing.lateDate, -1) : undefined;
  const lateMinDate = hasBoth && pricing.earlyBirdDate ? addDays(pricing.earlyBirdDate, 1) : undefined;

  const handleEarlyDateChange = useCallback((newEarlyDate) => {
    if (!hasBoth || !pricing.lateDate) return;
    if (newEarlyDate >= pricing.lateDate) {
      updateField("pricing", "lateDate", addDays(newEarlyDate, 1));
    }
  }, [hasBoth, pricing.lateDate, updateField]);

  const handleLateDateChange = useCallback((newLateDate) => {
    if (!hasBoth || !pricing.earlyBirdDate) return;
    if (newLateDate <= pricing.earlyBirdDate) {
      updateField("pricing", "earlyBirdDate", addDays(newLateDate, -1));
    }
  }, [hasBoth, pricing.earlyBirdDate, updateField]);

  return (
    <Content>
      <div>
        <Title className="merri">
          {hasBoth ? "Early & Late Pricing" : pricing.earlyBirdPricing ? "Early Pricing" : "Late Pricing"}
        </Title>
        <Subtitle className="raleway">
          Set time-based pricing for your program
        </Subtitle>
      </div>

      <FormCenter style={{ paddingBottom: 40 }}>
        <SectionsWrap>
          {pricing.earlyBirdPricing && (
            <PricingSection
              icon="fa-clock"
              label="Early Bird Pricing"
              feeField="earlyBirdFee"
              capField="earlyBirdFamilyCap"
              capValueField="earlyBirdFamilyCapValue"
              dateField="earlyBirdDate"
              dateLabel="Pay before"
              pricing={pricing}
              updateField={updateField}
              maxDate={earlyMaxDate}
              onDateChange={handleEarlyDateChange}
            />
          )}
          {pricing.latePricing && (
            <PricingSection
              icon="fa-hourglass-end"
              label="Late Pricing"
              feeField="lateFee"
              capField="lateFamilyCap"
              capValueField="lateFamilyCapValue"
              dateField="lateDate"
              dateLabel="Pay after"
              pricing={pricing}
              updateField={updateField}
              minDate={lateMinDate}
              onDateChange={handleLateDateChange}
            />
          )}
        </SectionsWrap>
      </FormCenter>
    </Content>
  );
}

function MemberCatechistPricing() {
  const { data, updateField, goToStep, setNextOverride, setBackOverride } = useWizard();
  const pricing = data.pricing;

  useEffect(() => {
    setNextOverride(() => {
      goToStep(5, 0, true);
      return true;
    });
    setBackOverride(() => {
      if (pricing.earlyBirdPricing || pricing.latePricing) {
        goToStep(4, 3);
      } else {
        goToStep(4, 2);
      }
      return true;
    });
    return () => {
      setNextOverride(null);
      setBackOverride(null);
    };
  }, [setNextOverride, setBackOverride, goToStep, pricing.earlyBirdPricing, pricing.latePricing]);

  return (
    <Content>
      <div>
        <Title className="merri">Other Discounts</Title>
        <Subtitle className="raleway">
          Set role-based pricing for your program
        </Subtitle>
      </div>

      <FormCenter>
        <SectionsWrap>
          {pricing.memberPricing && (
            <PricingSection
              icon="fa-id-card"
              label="Member Pricing"
              description="Members of your parish can register at a discounted rate"
              feeField="memberFee"
              capField="memberFamilyCap"
              capValueField="memberFamilyCapValue"
              pricing={pricing}
              updateField={updateField}
            />
          )}
          {pricing.catechistPricing && (
            <PricingSection
              icon="fa-chalkboard-user"
              label="Catechist Pricing"
              description="Volunteer catechists and their families can register at a reduced rate"
              feeField="catechistFee"
              capField="catechistFamilyCap"
              capValueField="catechistFamilyCapValue"
              pricing={pricing}
              updateField={updateField}
            />
          )}
        </SectionsWrap>
      </FormCenter>
    </Content>
  );
}

export default function Pricing({ subStep }) {
  if (subStep === 0) return <GeneralPricing />;
  if (subStep === 1) return <AdditionalFees />;
  if (subStep === 2) return <DiscountsAndMore />;
  if (subStep === 3) return <EarlyLatePricing />;
  return <MemberCatechistPricing />;
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
`;

const FormCenter = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const Title = styled.h2`
  font-size: 32px;
  margin: 0;
  color: #182941;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #6b7280;
  margin: 4px 0 0 0;
`;

const PricingList = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 480px;
`;

const PricingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  background: ${(p) => (p.$alt ? "#f5f5f5" : "#fff")};
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  cursor: ${(p) => (p.onClick ? "pointer" : "default")};
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }
`;

const RowIcon = styled.i`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${ACCENT_COLOR};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  font-size: 20px;
`;

const RowContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const RowLabel = styled.div`
  font-size: 18px;
  color: #182941;
`;

const PriceFieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  max-width: 190px;
`;

const PriceInputWrap = styled.div`
  display: inline-flex;
  align-items: center;
  background: #fff;
  border: 1px solid ${(p) => (p.$error ? "#f59e0b" : "#d1d5db")};
  border-radius: 10px;
  border-bottom-left-radius: ${(p) => (p.$error ? "0" : "10px")};
  border-bottom-right-radius: ${(p) => (p.$error ? "0" : "10px")};
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s, border-bottom-left-radius 0.2s, border-bottom-right-radius 0.2s;
  position: relative;
  z-index: 1;

  &:hover {
    box-shadow: 0 0 4px 2px ${(p) => (p.$error ? "rgba(245, 158, 11, 0.3)" : "rgba(0, 0, 0, 0.15)")};
  }

  &:focus-within {
    box-shadow: 0 0 4px 2px ${(p) => (p.$error ? "#f59e0b" : ACCENT_COLOR)};
  }
`;

const PricePrefix = styled.span`
  font-size: 22px;
  color: ${(p) => (p.$active ? "#000" : "#d1d5db")};
  padding-left: 14px;
  user-select: none;
  transition: color 0.15s;
`;

const PriceInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  font-size: 22px;
  font-family: inherit;
  padding: 12px 14px 12px 6px;
  background: transparent;

  &::placeholder {
    color: #d1d5db;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const PricingCheckbox = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 2px solid ${(p) => (p.$on ? ACCENT_COLOR : "#d1d5db")};
  background: ${(p) => (p.$on ? ACCENT_COLOR : "#fff")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
  color: #fff;
  font-size: 14px;
`;

const PriceError = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px 5px;
  background: #fef9e7;
  border: 1px solid #f59e0b;
  border-top: none;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  color: #b45309;
  font-size: 13px;
  line-height: 1.2;
  text-align: center;
  min-width: 0;
`;

const HelpLink = styled.a`
  color: #9ca3af;
  font-size: 14px;
  transition: color 0.15s, transform 0.15s;
  display: inline;

  &:hover {
    color: ${ACCENT_COLOR};
  }

  &:active {
    transform: scale(0.85);
  }
`;

const RequirePaymentDesc = styled.div`
  font-size: 15px;
  color: #6b7280;
  line-height: 1.15;
`;

const FamilyCapRow = styled(PricingRow)`
  cursor: pointer;
  overflow: hidden;
`;

const FamilyCapTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
`;

const FamilyCapInputInner = styled.div`
  cursor: default;
`;

const ChoiceRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ChoiceRow = styled.div`
  display: flex;
  align-items: center;
  padding: 22px 20px;
  background: ${(p) => (p.$selected ? "rgba(1, 96, 153, 0.04)" : "#fff")};
  border: 2px solid ${(p) => (p.$selected ? ACCENT_COLOR : "#e5e7eb")};
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: border-color 0.2s, box-shadow 0.2s, background 0.15s;
  user-select: none;

  &:hover {
    border-color: ${ACCENT_COLOR};
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
  }

  &:active {
    background: #f7f7f8;
  }

  &:active ${() => ChoiceRowInner} {
    transform: scale(0.97);
  }
`;

const ChoiceRowInner = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  transition: transform 0.15s;
`;

const ChoiceIcon = styled.i`
  font-size: 38px;
  flex-shrink: 0;
  color: ${ACCENT_COLOR};
`;

const ChoiceLabel = styled.div`
  font-size: 22px;
  color: #182941;
`;

const CheckIcon = styled.i`
  font-size: 38px;
  color: #00B44E;
  margin-left: auto;
  flex-shrink: 0;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 0.2s;
`;

const FeesList = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 520px;
`;

const FeeRow = styled.div`
  background: ${(p) => (p.$alt ? "#f5f5f5" : "#fff")};
  border-bottom: ${(p) => (p.$last ? "none" : "1px solid rgba(0, 0, 0, 0.06)")};
`;

const FeeRowTop = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 24px;
  min-height: 60px;
  gap: 12px;
`;

const FeeClassName = styled.div`
  font-size: 18px;
  color: #182941;
  flex: 1;
`;

const FeePill = styled.button`
  appearance: none;
  border: none;
  border-radius: 100px;
  padding: 0;
  height: 28px;
  width: ${(p) => (p.$active ? "34px" : "78px")};
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  transition: background 0.2s, color 0.2s, width 0.2s;
  background: ${(p) => (p.$active ? "rgba(239, 68, 68, 0.08)" : "rgba(1, 96, 153, 0.08)")};
  color: ${(p) => (p.$active ? "#dc2626" : ACCENT_COLOR)};

  &:hover {
    background: ${(p) => (p.$active ? "rgba(239, 68, 68, 0.15)" : "rgba(1, 96, 153, 0.15)")};
  }

  &:active ${() => PillInner} {
    transform: scale(0.85);
  }
`;

const PillInner = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s;
`;

const PillContent = styled(motion.span)`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;

const FeeFieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 130px;
`;

const FeeInputWrap = styled.div`
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid ${(p) => (p.$error ? "#f59e0b" : "#d1d5db")};
  border-radius: 8px;
  border-bottom-left-radius: ${(p) => (p.$error ? "0" : "8px")};
  border-bottom-right-radius: ${(p) => (p.$error ? "0" : "8px")};
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s, border-bottom-left-radius 0.2s, border-bottom-right-radius 0.2s;
  position: relative;
  z-index: 1;

  &:hover {
    box-shadow: 0 0 3px 1px ${(p) => (p.$error ? "rgba(245, 158, 11, 0.3)" : "rgba(0, 0, 0, 0.15)")};
  }

  &:focus-within {
    box-shadow: 0 0 3px 2px ${(p) => (p.$error ? "#f59e0b" : ACCENT_COLOR)};
  }
`;

const FeePrefix = styled.span`
  font-size: 16px;
  color: ${(p) => (p.$active ? "#000" : "#d1d5db")};
  padding-left: 10px;
  user-select: none;
  transition: color 0.15s;
`;

const FeeInput = styled.input`
  width: 90px;
  border: none;
  outline: none;
  font-size: 16px;
  font-family: inherit;
  padding: 7px 10px 7px 4px;
  background: transparent;

  &::placeholder {
    color: #d1d5db;
  }
`;

const FeeError = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px 4px;
  background: #fef9e7;
  border: 1px solid #f59e0b;
  border-top: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  color: #b45309;
  font-size: 11px;
  line-height: 1.2;
  text-align: center;
`;

const DiscountDesc = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-top: -1px;
`;

const DiscountPickerPortal = styled(motion.div)`
  position: fixed;
  z-index: 10000;
  perspective: 1200px;
`;

const DateInputWrap = styled.div`
  display: inline-flex;
  align-items: center;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    box-shadow: 0 0 4px 2px rgba(0, 0, 0, 0.15);
  }

  ${(p) => p.$active && `box-shadow: 0 0 4px 2px ${ACCENT_COLOR}; border-color: ${ACCENT_COLOR};`}

  i {
    color: ${(p) => (p.$active ? ACCENT_COLOR : "#d1d5db")};
    font-size: 22px;
    flex-shrink: 0;
    padding-right: 14px;
    transition: color 0.15s;
  }
`;

const DateInputText = styled.span`
  font-size: 22px;
  padding: 12px 6px 12px 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) => (p.$hasValue ? "#182941" : "#d1d5db")};
`;

const CategoryRow = styled(PricingRow)`
  cursor: pointer;
  user-select: none;
  padding: 16px 24px;
`;

const CategoryLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
  width: 100%;
  max-width: 480px;
  text-align: center;
  margin-bottom: -8px;
`;

const AdvancedButtonInner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.15s;
`;

const AdvancedButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  font-size: 14px;
  color: #9ca3af;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 100px;
  font-family: inherit;
  align-self: center;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: ${ACCENT_COLOR};
    background: rgba(1, 96, 153, 0.06);
  }

  &:active ${AdvancedButtonInner} {
    transform: scale(0.93);
  }
`;

const SectionsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 480px;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
  text-align: center;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const SectionTitleIcon = styled.i`
  font-size: 13px;
`;

const SectionDescRow = styled.div`
  padding: 12px 24px;
  font-size: 15px;
  color: #6b7280;
  line-height: 1.4;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;


const ModalBackdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalCard = styled(motion.div)`
  background: #fff;
  border-radius: 20px;
  padding: 32px;
  max-width: 520px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
`;

const ModalCloseBtn = styled.button`
  appearance: none;
  border: none;
  background: none;
  position: absolute;
  top: 14px;
  right: 12px;
  padding: 0;
  cursor: pointer;
  color: #c4c4c4;
  font-size: 32px;
  line-height: 1;
  z-index: 10;
  transition: color 0.2s, transform 0.15s;

  &:hover {
    color: #888;
  }

  &:active {
    transform: scale(0.9);
    color: #666;
  }
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  margin: 0;
  color: #182941;
`;

const ModalText = styled.p`
  font-size: 15px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

const ModalOptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-radius: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  user-select: none;
`;

const ModalOptionIcon = styled.i`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${ACCENT_COLOR};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  font-size: 18px;
`;

const ModalOptionLabel = styled.div`
  flex: 1;
  font-size: 16px;
  color: #182941;
  line-height: 1.4;
`;

const ModalOkWrap = styled.div`
  align-self: flex-end;
  background: ${(p) => (p.$disabled ? "#d1d5db" : ACCENT_COLOR)};
  border-radius: 100px;
  transition: background 0.2s;
  pointer-events: ${(p) => (p.$disabled ? "none" : "auto")};
`;

const ModalOkInside = styled.div`
  font-size: 20px;
  color: #fff;
  text-shadow: rgba(0, 0, 0, 0.2) 1px 1px 3px;
  padding: 10px 10px 10px 22px;
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    font-size: 24px;
  }
`;

